/**
 * BuildingControls - Arrow pad and rotation controls for building placement
 *
 * Provides precise movement and rotation controls relative to map orientation.
 * Supports hold-to-repeat for continuous movement while button is held.
 */

import { useRef, useCallback, useEffect } from 'react'
import { ArrowUp, ArrowDown, ArrowLeft, ArrowRight, RotateCcw, RotateCw } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useBuildingEditorStore } from '@/stores/buildingEditorStore'

interface BuildingControlsProps {
  mapBearing?: number // Current map rotation in degrees
  initialBearing?: number // Location's default orientation (from viewSettings)
}

const MOVE_STEP = 5 // feet per click/tick
const ROTATE_STEP = 5 // degrees per click/tick
const REPEAT_DELAY = 200 // ms before repeat starts
const REPEAT_INTERVAL = 50 // ms between repeats

export function BuildingControls({ mapBearing = 0, initialBearing = 0 }: BuildingControlsProps) {
  const { previewBuilding, updatePreviewWorldPosition, updatePreviewRotation, mode } =
    useBuildingEditorStore()

  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Clear any active timers
  const clearTimers = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
      intervalRef.current = null
    }
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }
  }, [])

  // Cleanup on unmount
  useEffect(() => {
    return () => clearTimers()
  }, [clearTimers])

  /**
   * Move building in direction relative to map bearing (camera orientation)
   * Uses world coordinates directly (feet)
   *
   * World coordinate system:
   * - +X = east, -X = west
   * - +Z = south, -Z = north
   *
   * Mapbox bearing:
   * - bearing = 0: north is up on screen
   * - bearing = 90: east is up on screen (map rotated 90° clockwise)
   *
   * When user presses "up", they want to move toward top of screen,
   * which is the direction that is `bearing` degrees clockwise from north.
   */
  const move = useCallback(
    (direction: 'up' | 'down' | 'left' | 'right') => {
      if (!previewBuilding) return

      // Convert bearing to radians with offset correction
      // The offset comes from the location's initial bearing (viewSettings.bearing)
      // This aligns screen directions with world movement regardless of default orientation
      const adjustedBearing = mapBearing - initialBearing
      const bearingRad = (adjustedBearing * Math.PI) / 180

      // Debug: log bearing value
      console.log('Arrow move:', { direction, mapBearing, initialBearing, adjustedBearing, bearingRad: bearingRad.toFixed(2) })

      let worldDx = 0
      let worldDz = 0

      // "Up" on screen = direction that is `bearing` degrees clockwise from north
      // At bearing=0: up=north=-Z, right=east=+X
      // At bearing=90: up=east=+X, right=south=+Z
      switch (direction) {
        case 'up':
          worldDx = Math.sin(bearingRad) * MOVE_STEP
          worldDz = -Math.cos(bearingRad) * MOVE_STEP
          break
        case 'down':
          worldDx = -Math.sin(bearingRad) * MOVE_STEP
          worldDz = Math.cos(bearingRad) * MOVE_STEP
          break
        case 'left':
          worldDx = -Math.cos(bearingRad) * MOVE_STEP
          worldDz = -Math.sin(bearingRad) * MOVE_STEP
          break
        case 'right':
          worldDx = Math.cos(bearingRad) * MOVE_STEP
          worldDz = Math.sin(bearingRad) * MOVE_STEP
          break
      }

      console.log('Arrow result:', { worldDx: worldDx.toFixed(2), worldDz: worldDz.toFixed(2) })

      // Update world coordinates directly (feet) - GPS is derived in store
      const newWorldX = previewBuilding.worldX + worldDx
      const newWorldZ = previewBuilding.worldZ + worldDz

      updatePreviewWorldPosition(newWorldX, newWorldZ)
    },
    [previewBuilding, mapBearing, initialBearing, updatePreviewWorldPosition]
  )

  /**
   * Rotate building clockwise or counter-clockwise
   */
  const rotate = useCallback(
    (direction: 'cw' | 'ccw') => {
      if (!previewBuilding) return

      const delta = direction === 'cw' ? ROTATE_STEP : -ROTATE_STEP
      let newBearing = previewBuilding.bearing_deg + delta

      // Normalize to 0-360
      newBearing = ((newBearing % 360) + 360) % 360

      updatePreviewRotation(newBearing)
    },
    [previewBuilding, updatePreviewRotation]
  )

  /**
   * Start hold-to-repeat for movement
   */
  const startMoveRepeat = useCallback(
    (direction: 'up' | 'down' | 'left' | 'right') => {
      // Immediate move
      move(direction)

      // Start repeat after delay
      timeoutRef.current = setTimeout(() => {
        intervalRef.current = setInterval(() => {
          move(direction)
        }, REPEAT_INTERVAL)
      }, REPEAT_DELAY)
    },
    [move]
  )

  /**
   * Start hold-to-repeat for rotation
   */
  const startRotateRepeat = useCallback(
    (direction: 'cw' | 'ccw') => {
      // Immediate rotate
      rotate(direction)

      // Start repeat after delay
      timeoutRef.current = setTimeout(() => {
        intervalRef.current = setInterval(() => {
          rotate(direction)
        }, REPEAT_INTERVAL)
      }, REPEAT_DELAY)
    },
    [rotate]
  )

  // Don't show controls in view mode or without preview building
  if (mode === 'view' || !previewBuilding) {
    return null
  }

  return (
    <div className="absolute bottom-20 left-4 z-10 bg-background/90 p-2 rounded-lg shadow-md backdrop-blur-sm">
      {/* Movement pad */}
      <div className="grid grid-cols-3 gap-1">
        <div />
        <Button
          size="icon"
          variant="outline"
          onMouseDown={() => startMoveRepeat('up')}
          onMouseUp={clearTimers}
          onMouseLeave={clearTimers}
          className="h-9 w-9"
          title="Move forward"
        >
          <ArrowUp className="h-4 w-4" />
        </Button>
        <div />
        <Button
          size="icon"
          variant="outline"
          onMouseDown={() => startMoveRepeat('left')}
          onMouseUp={clearTimers}
          onMouseLeave={clearTimers}
          className="h-9 w-9"
          title="Move left"
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="h-9 w-9" />
        <Button
          size="icon"
          variant="outline"
          onMouseDown={() => startMoveRepeat('right')}
          onMouseUp={clearTimers}
          onMouseLeave={clearTimers}
          className="h-9 w-9"
          title="Move right"
        >
          <ArrowRight className="h-4 w-4" />
        </Button>
        <div />
        <Button
          size="icon"
          variant="outline"
          onMouseDown={() => startMoveRepeat('down')}
          onMouseUp={clearTimers}
          onMouseLeave={clearTimers}
          className="h-9 w-9"
          title="Move backward"
        >
          <ArrowDown className="h-4 w-4" />
        </Button>
        <div />
      </div>

      {/* Rotation controls */}
      <div className="flex gap-1 mt-2 justify-center">
        <Button
          size="icon"
          variant="outline"
          onMouseDown={() => startRotateRepeat('ccw')}
          onMouseUp={clearTimers}
          onMouseLeave={clearTimers}
          className="h-9 w-9"
          title="Rotate counter-clockwise"
        >
          <RotateCcw className="h-4 w-4" />
        </Button>
        <Button
          size="icon"
          variant="outline"
          onMouseDown={() => startRotateRepeat('cw')}
          onMouseUp={clearTimers}
          onMouseLeave={clearTimers}
          className="h-9 w-9"
          title="Rotate clockwise"
        >
          <RotateCw className="h-4 w-4" />
        </Button>
      </div>

      {/* Movement info */}
      <div className="mt-2 text-xs text-center text-muted-foreground">
        {MOVE_STEP}ft / {ROTATE_STEP}°
      </div>
    </div>
  )
}
